/**
 * Follow-ups — what a row's follow-up controls DO (redesign Phase 1A, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-redesign-plan.md
 *
 * A behaviour port of src/pages/Admin/AdminTable.vue's methods, which is FROZEN while the redesign
 * is built next to it. Same store actions, same payloads, same row patches:
 *
 *   setInitialDate   ← saveInitialFollowupDate      tasks/updateInitialFollowupDate
 *   changeDate       ← changeFollowupDate           tasks/updateFollowupDate
 *   removeInitial    ← _removeInitialFollowupDate   tasks/removeInitialFollowupDate
 *   stopFollowups    ← _stopFollowups               tasks/stopFollowups
 *   restartFollowups ← _resetFollowups              tasks/resetFollowups
 *   openDrawer       ← createFollowup               teamio/startTrackingFUAttempt → $showPanel(CreateFollowup)
 *   aiCall           ← onRowAICall                  triggerAutorecallCall
 *   install/dispose  ← created / beforeDestroy      cache/emergencyCleanupAllSessions
 *
 * tests/component/FollowupsActionsAgreement.spec.js runs the REAL legacy AdminTable.vue and this
 * file over the same rows and inputs and fails if a store action name, a payload, the $showPanel
 * options, the drawer query or the row patch differ. Fix THIS file to match the legacy one.
 *
 * ── WHAT IS DELIBERATELY DIFFERENT ─────────────────────────────────────────────────────────────
 *   • State is keyed by ROW ID (queues.js rowIdOf), not by the row's index on the current page.
 *     Legacy kept `followupDates[i]`, `loadingStatus[i]`… by index, so a filter change between
 *     opening and saving could put the spinner (or the date) on another patient.
 *   • The legacy table never says a save failed (console only). Each save here also toasts — on
 *     success with the design's wording, on failure with a retry hint.
 *   • The "already open" lock path no longer scrolls the page to the top (window.scrollTo(0,0)):
 *     the queue can live inside a Schedule View tab, where that jump loses the user's place.
 *   • A second click on the same patient while the drawer is opening is ignored (legacy started a
 *     second lock attempt, which then reported "someone is already following up" — yourself).
 *   • If $showPanel itself throws after the lock was taken, the lock is released
 *     (stopTrackingFUAttempt) as well as forgotten locally; legacy only forgot it locally.
 *   • A missing responsible-employee setting sends AssignedToEmpID null instead of throwing.
 *
 * ── KEPT ON PURPOSE ────────────────────────────────────────────────────────────────────────────
 *   • AssignedToEmpID falls back to the RAW responsible-employee setting — "12" or "12|19" for an
 *     office with two responsible employees (getAssignedToEmployeeID). The backend has always
 *     received that string; changing it is a backend conversation, not a UI redesign.
 *   • changeDate's OldFollowupDate is moment(FollowupDate).format('MM-DD-YYYY') — 'Invalid date'
 *     for a null date — and removeInitial's is 'YYYY-MM-DD'. Both exactly as legacy sends them.
 *   • setInitialDate sends TeamioPatientID `${siteID}-${PatNum}` (not the TeamioPatientID
 *     fallback the other saves use) and the raw ClaimNum-or-null; changeDate sends ClaimNum raw.
 *   • The employee pre-selected for a save is the row's manually assigned one, on EVERY queue
 *     (legacy's @shown → setSelectedEmployee ran whether or not the picker was visible).
 *
 * ── PATIENT PROFILE (mode 'profile', Phase 5, 2026-10-02) ─────────────────────────────────────
 * One patient's rows from every queue. Each row uses ITS OWN queue (queueByTaskTypeID) for the
 * task type, the responsible employees and the drawer query; the row id is its TaskID; the patient
 * id is host.profilePatientId (the rows carry no PatNum). And the saves follow Patient Profile's
 * own copy of this logic (PatientProfile.vue), not AdminTable's, where the two differ —
 * tests/component/PatientProfileFollowupsAgreement.spec.js runs the real Patient Profile methods:
 *   changeDate       OldFollowupDate 'YYYY-MM-DD' (AdminTable 'MM-DD-YYYY'); AssignedToEmpID /
 *                    EmployeeManuallyAssigned are the ROW's own (AdminTable: the picker, else the
 *                    raw setting and 0) unless the Assign-to picker was changed (aging rows only —
 *                    new: Patient Profile has no picker; a cleared picker sends AdminTable's values)
 *   removeInitial    the row is REMOVED from `rows` (Patient Profile splices it out)
 *   restartFollowups no `completed` patch
 *   openDrawer       the drawer gets the `query` (CreateFollowup runs it, as from AdminTable)
 *                    instead of Patient Profile's pre-fetched `results`; on close, no Overridden
 *                    patch, teamioActive = false, and no call-goals-updated (the profile has no goals)
 *
 * ── WHICH DRAWER (drawer plan Phase 6c, 2026-10-02) ────────────────────────────────────────────
 * openDrawer opens the NEW drawer (src/pages/Followups/FollowupDrawer.vue) when
 * shouldUseNewFollowupsUi('drawer', …) says so — Super Admins by default (with their own drawer
 * override, uiVersion DRAWER_STORAGE_KEY), office users per STAGE.drawer and their one office
 * choice (no "Classic drawer" link for them) — and today's CreateFollowup.vue
 * otherwise, with the SAME $showPanel call as before 6c (component, DRAWER_PANEL_OPTIONS, props).
 * The lock, the session bookkeeping, the query and the row patch are shared by both. The new drawer
 * also gets two callbacks: onClassicDrawer (its Super Admin "Classic drawer" link: after the close
 * and the lock release, the same row is reopened here — now in the classic drawer) and onRestart
 * (the alert's "Restart follow-ups" runs restartFollowups below on the same row).
 *
 * The host is the FollowupsQueue instance (anything with the same members works — the specs pass
 * a plain object): $store, $showPanel, $toast, $can, $emit, siteID, userID, user, allSettings,
 * activeEmployees, dateFormatStr, queueDef, rows, listId, mode, profilePatientId.
 */
import Vue from 'vue';
import moment from 'moment-timezone';
import CreateFollowup from '@/pages/Admin/components/CreateFollowup.vue';
import FollowupDrawer from '@/pages/Followups/FollowupDrawer.vue';
import ToastComponent from '@/pages/Admin/components/ToastComponent.vue';
import { triggerAutorecallCall } from '@/api/autorecall';
import {
  rowIdOf, teamioPatientIdOf, buildDrawerQuery, responsibleEmployeeIds, queueByTaskTypeID, profileRowIdOf,
} from '@/utils/followups/queues.js';
import { formatDate } from '@/utils/followups/rowModel.js';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';

/** The drawer's slide-out options, exactly as AdminTable.createFollowup passes them. */
export const DRAWER_PANEL_OPTIONS = {
  openOn: 'right',
  width: 1020,
  disableBgClick: true,
  disableEscClick: true,
  cssClass: 'followUpPanelClass',
};

/**
 * The NEW drawer's slide-out options (the classic drawer keeps DRAWER_PANEL_OPTIONS). Its own class
 * (no followUpPanelClass padding / background). The panel library closes on its own Escape /
 * background click with a result of `{ closedBy }` — not the drawer's close result — so both stay
 * disabled here, as today; FollowupDrawer.vue closes ITSELF on Escape, and only when nothing is
 * being typed or saved (see its onKeydown).
 */
export const NEW_DRAWER_PANEL_OPTIONS = {
  openOn: 'right',
  disableBgClick: true,
  disableEscClick: true,
  cssClass: 'fu-drawer-panel',
};

/** The design's drawer width, min(1080px, 94%) — and the whole screen below 640px. */
export const NEW_DRAWER_MAX_WIDTH = 1080;
export const NEW_DRAWER_PHONE_BELOW = 640;

/**
 * The new drawer's width in px for a viewport. The panel library only takes a px width (and makes
 * the panel 100% wide on any screen no wider than it), so the design's min(1080px, 94%) is worked
 * out when the drawer opens: 1080 on a wide screen, 94% of it on a narrower one, and the full
 * screen width on a phone (the library's own rule then makes it 100%).
 */
export function newDrawerWidth(viewportWidth) {
  const w = Math.floor(Number(viewportWidth));
  if (!Number.isFinite(w) || w <= 0) return NEW_DRAWER_MAX_WIDTH;
  if (w < NEW_DRAWER_PHONE_BELOW) return w;
  return Math.min(NEW_DRAWER_MAX_WIDTH, Math.floor(w * 0.94));
}

function viewportWidth() {
  try {
    return (window.document && window.document.documentElement && window.document.documentElement.clientWidth) || window.innerWidth || 0;
  } catch (e) {
    return 0;
  }
}

/** The copy legacy shows beside the name when the drawer could not be opened. */
export const OPEN_ERROR_TEXT = 'Something went wrong while trying to make a follow-up attempt. Please try again in a moment.';

const SAVE_ERROR_TEXT = 'The follow-up could not be saved. Please try again in a moment.';

function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

/**
 * @param {Object} host  see the header
 * @returns the action API; `state` is reactive ({ saving, errors, opening, aiCalling } maps by row id)
 */
export function createFollowupActions(host) {
  const state = Vue.observable({ saving: {}, errors: {}, opening: {}, aiCalling: {} });

  const q = () => host.queueDef || {};
  const isProfile = () => host.mode === 'profile';
  // The queue whose rules apply to THIS row: its own on Patient Profile, the page's everywhere else.
  const qOf = row => (isProfile() ? (queueByTaskTypeID((row || {}).TaskTypeID) || {}) : q());
  const idOf = row => (isProfile() ? profileRowIdOf(row) : rowIdOf(q(), row, host.siteID));
  const patientIdOf = row => (isProfile() ? host.profilePatientId : teamioPatientIdOf(row, host.siteID));
  const setFlag = (map, id, on) => {
    if (id === null) return;
    if (on) Vue.set(state[map], id, true);
    else Vue.delete(state[map], id);
  };

  /** The row object to patch: the one in `rows` with the same id (legacy getOriginalDataIndex), else `row` itself. */
  function findRow(row) {
    const id = idOf(row);
    const rows = host.rows || [];
    if (id !== null) {
      for (let i = 0; i < rows.length; i += 1) {
        if (idOf(rows[i]) === id) return { target: rows[i], index: i };
      }
    }
    const index = rows.indexOf(row);
    return { target: row, index };
  }

  /**
   * getAssignedToEmployeeID — the raw responsible-employee setting ("id" or "id|id|…"), or null.
   * `row` picks the queue on Patient Profile; elsewhere it is ignored (the page's queue).
   */
  function defaultAssignedTo(row) {
    const s = host.allSettings && host.allSettings[qOf(row).responsibleEmployeeSetting];
    return s ? s.PropertyValue : null;
  }

  /** filteredEmployees — the active employees named in the responsible-employee setting. */
  function responsibleEmployees(row) {
    const ids = responsibleEmployeeIds(qOf(row), host.allSettings);
    if (!ids.length) return [];
    return (host.activeEmployees || []).filter(e => ids.includes(e.EmployeeID));
  }

  /** setSelectedEmployee — the row's manually assigned employee, when it is one of the above. */
  function preselectedEmployee(row) {
    const r = row || {};
    if (r.EmployeeManuallyAssigned !== 1) return null;
    return responsibleEmployees(row).find(e => e.EmployeeID === r.AssignedToEmpID) || null;
  }

  /** getBelongsToListID — the row's list on the two main pages, the page's list everywhere else. */
  function belongsToListID(row) {
    const key = q().key;
    if (host.mode === 'page' && (key === 'overdueRecall' || key === 'unscheduledTreatment')) {
      return row.BelongsToListID;
    }
    return host.listId;
  }

  function fmt(ymd) {
    return formatDate(ymd, host.dateFormatStr);
  }

  async function runSave(row, action, payload, patch, successText) {
    const id = idOf(row);
    setFlag('saving', id, true);
    try {
      await host.$store.dispatch(action, payload);
      const { target } = findRow(row);
      patch(target);
      if (host.$toast && successText) host.$toast.success(successText);
      return true;
    } catch (err) {
      console.error(err);
      if (host.$toast) host.$toast.error(SAVE_ERROR_TEXT);
      return false;
    } finally {
      setFlag('saving', id, false);
    }
  }

  /** "Start following up on" — the first follow-up date (+ note, + employee on aging queues). */
  function setInitialDate(row, { date, note, employee } = {}) {
    if (!date) return Promise.resolve(false);
    const user = host.user || {};
    const day = moment(date).format('YYYY-MM-DD');
    const payload = {
      CreatedAt: moment().format('YYYY-MM-DD'),
      SiteID: host.siteID,
      CreatedAutomatically: 1,
      AssignedToEmpID: employee ? employee.EmployeeID : defaultAssignedTo(),
      DueDate: day,
      StatusID: 1,
      TaskTypeID: q().taskTypeID,
      PatNum: row.PatNum,
      UserODNum: user.ODUserNum,
      DefaultDB: user.DefaultDB,
      TeamioPatientID: `${host.siteID}-${row.PatNum}`,
      FollowupDate: day,
      Active: 1,
      Attempts: 0,
      BelongsToListID: belongsToListID(row),
      ClaimNum: row.ClaimNum ? row.ClaimNum : null,
      InitialFollowupDateSet: 1,
      InitialFollowupCreatedOn: moment().format('YYYY-MM-DD'),
      EmployeeManuallyAssigned: employee ? 1 : 0,
      ...(note && { initialFollowupNote: note }),
    };
    return runSave(row, 'tasks/updateInitialFollowupDate', payload, (t) => {
      Vue.set(t, 'FollowupDate', day);
      Vue.set(t, 'InitialFollowupDateSet', 1);
      Vue.set(t, 'CreatedAutomatically', 1);
      if (employee) {
        Vue.set(t, 'EmployeeManuallyAssigned', 1);
        Vue.set(t, 'AssignedToEmpID', employee.EmployeeID);
      }
      if (note) Vue.set(t, 'InitialFollowupNote', note);
    }, `Follow-ups started — first follow-up ${fmt(day)}`);
  }

  /**
   * "Change follow-up date to" (+ employee on aging queues).
   * On Patient Profile `employee` is three-valued: undefined = the picker was not touched (or not
   * shown) → the row's own assignment, as Patient Profile sends it; an employee → that one; null =
   * the picker was cleared → AdminTable's rule (the raw setting, not manually assigned).
   */
  function changeDate(row, { date, employee } = {}) {
    if (!date) return Promise.resolve(false);
    const day = moment(date).format('YYYY-MM-DD');
    if (isProfile()) return changeDateProfile(row, day, employee);
    const payload = {
      SiteID: host.siteID,
      EmployeeID: host.userID,
      TaskTypeID: q().taskTypeID,
      AssignedToEmpID: employee ? employee.EmployeeID : defaultAssignedTo(),
      TeamioPatientID: teamioPatientIdOf(row, host.siteID),
      FollowupDate: day,
      OldFollowupDate: moment(row.FollowupDate).format('MM-DD-YYYY'),
      EmployeeManuallyAssigned: employee ? 1 : 0,
      ClaimNum: row.ClaimNum,
    };
    return runSave(row, 'tasks/updateFollowupDate', payload, (t) => {
      Vue.set(t, 'FollowupDate', day);
      Vue.set(t, 'pastDue', 'false');
      if (employee) {
        Vue.set(t, 'EmployeeManuallyAssigned', 1);
        Vue.set(t, 'AssignedToEmpID', employee.EmployeeID);
      }
      if (moment(day).isSame(moment(), 'day')) Vue.set(t, 'dueToday', 'true');
    }, `Follow-up moved to ${fmt(day)}`);
  }

  /**
   * Patient Profile's changeFollowupDate payload and patch. ⚠ Patient Profile only saves when the
   * row already HAS a date (`if (ufp.FollowupDate)`) — on a row with none its Save silently does
   * nothing. Here that save goes through (OldFollowupDate 'Invalid date', exactly what AdminTable
   * has always sent for a dateless row): a Save that does nothing is a bug, not a rule.
   */
  function changeDateProfile(row, day, employee) {
    let assignedTo = row.AssignedToEmpID;
    let manual = row.EmployeeManuallyAssigned;
    if (employee) {
      assignedTo = employee.EmployeeID;
      manual = 1;
    } else if (employee === null) {
      assignedTo = defaultAssignedTo(row);
      manual = 0;
    }
    const payload = {
      SiteID: host.siteID,
      EmployeeID: host.userID,
      EmployeeManuallyAssigned: manual,
      AssignedToEmpID: assignedTo,
      TaskTypeID: row.TaskTypeID,
      TeamioPatientID: patientIdOf(row),
      FollowupDate: day,
      OldFollowupDate: moment(row.FollowupDate).format('YYYY-MM-DD'),
      ClaimNum: row.ClaimNum,
    };
    return runSave(row, 'tasks/updateFollowupDate', payload, (t) => {
      Vue.set(t, 'FollowupDate', day);
      Vue.set(t, 'pastDue', 'false');
      if (moment(day).isSame(moment(), 'day')) Vue.set(t, 'dueToday', 'true');
      if (employee) {
        Vue.set(t, 'EmployeeManuallyAssigned', 1);
        Vue.set(t, 'AssignedToEmpID', employee.EmployeeID);
      } else if (employee === null) {
        Vue.set(t, 'EmployeeManuallyAssigned', 0);
        Vue.set(t, 'AssignedToEmpID', assignedTo);
      }
    }, `Follow-up moved to ${fmt(day)}`);
  }

  function basePayload(row) {
    return {
      SiteID: host.siteID,
      TeamioPatientID: patientIdOf(row),
      ClaimNum: row.ClaimNum ? row.ClaimNum : null,
      TaskTypeID: isProfile() ? row.TaskTypeID : q().taskTypeID,
      EmployeeID: host.userID,
    };
  }

  /** "Remove Initial Set Follow-up" — an untouched, automatically created first follow-up. */
  function removeInitial(row) {
    const payload = Object.assign(basePayload(row), {
      OldFollowupDate: moment(row.FollowupDate).format('YYYY-MM-DD'),
    });
    if (isProfile()) {
      // Patient Profile takes the row off the list (upcomingFollowups.splice(index, 1)).
      return runSave(row, 'tasks/removeInitialFollowupDate', payload, () => {
        const { index } = findRow(row);
        if (index !== -1) host.rows.splice(index, 1);
      }, 'Initial follow-up removed');
    }
    return runSave(row, 'tasks/removeInitialFollowupDate', payload, (t) => {
      Vue.set(t, 'FollowupDate', null);
      Vue.set(t, 'Status', 'Not Called');
      Vue.set(t, 'InitialFollowupDateSet', null);
      Vue.set(t, 'CreatedAutomatically', null);
      Vue.set(t, 'pastDue', false);
      Vue.set(t, 'InitialFollowupNote', null);
    }, 'Initial follow-up removed');
  }

  /** "Stop Follow-ups". */
  function stopFollowups(row) {
    return runSave(row, 'tasks/stopFollowups', basePayload(row), (t) => {
      Vue.set(t, 'FollowupStopped', 1);
      Vue.set(t, 'FollowupDate', null);
    }, 'Follow-ups stopped');
  }

  /** "Restart the Follow-up Cycle". */
  function restartFollowups(row) {
    return runSave(row, 'tasks/resetFollowups', basePayload(row), (t) => {
      Vue.set(t, 'FollowupDate', null);
      Vue.set(t, 'Status', 'Not Called');
      Vue.set(t, 'Attempts', 0);
      Vue.set(t, 'FollowupStopped', 0);
      Vue.set(t, 'TeamioFollowupStatusID', null);
      // Patient Profile's _resetFollowups leaves `completed` alone.
      if (!isProfile()) Vue.set(t, 'completed', 'false');
    }, 'Follow-up cycle restarted');
  }

  /**
   * shouldUseNewFollowupsUi('drawer', …) for this host's user (exactly `true` for a Super Admin; an
   * office user follows STAGE.drawer and their one office choice, keyed by the host's employee ID).
   */
  function usesNewDrawer() {
    const isSuperAdmin = !!(host.user && host.user.Role === 'Super Admin');
    return shouldUseNewFollowupsUi('drawer', { isSuperAdmin, userKey: host.userID, storage: browserStorage() });
  }

  /**
   * Open the follow-up drawer for a row — the new one (FollowupDrawer.vue) behind the drawer gate,
   * today's CreateFollowup.vue otherwise — with the lock, the session bookkeeping, the query, the
   * slide-out, and patching the row when it closes.
   * Resolves once the drawer is OPEN (or refused); the close is handled in the background.
   */
  async function openDrawer(row) {
    const id = idOf(row);
    if (id !== null && state.opening[id]) return false;
    setFlag('opening', id, true);
    setFlag('errors', id, false);

    const teamioPatientID = patientIdOf(row);
    const rq = qOf(row);
    const claimNum = row.ClaimNum;
    const attemptTrackingPayload = {
      cacheName: process.env.VUE_APP_MOMENTO_CACHE,
      cacheSet: 'fuattempts',
      element: teamioPatientID,
    };
    let tracking = false;
    // Set by the new drawer's "Classic drawer" link: reopen this row in the classic drawer once the
    // close has been handled and the lock released.
    let reopenClassic = false;

    const release = async () => {
      if (!tracking) return;
      tracking = false;
      try {
        await host.$store.dispatch('teamio/stopTrackingFUAttempt', attemptTrackingPayload);
      } catch (cleanupError) {
        console.error('[FOLLOWUP] Error during cleanup:', cleanupError);
      } finally {
        host.$store.commit('cache/removeActiveFollowupSession', teamioPatientID);
      }
    };

    try {
      await host.$store.dispatch('teamio/startTrackingFUAttempt', attemptTrackingPayload);
      tracking = true;
      host.$store.commit('cache/addActiveFollowupSession', { patientId: teamioPatientID, payload: attemptTrackingPayload });

      const query = buildDrawerQuery(rq, {
        siteID: host.siteID,
        teamioPatientID,
        claimNum,
        today: moment().format('YYYY-MM-DD'),
        country: readStorage('country'),
        brokenApptAdjustments: readStorage('brokenApptAdjustments'),
        brokenApptCodes: readStorage('brokenApptCodes'),
      });

      let handle;
      if (usesNewDrawer()) {
        handle = host.$showPanel(Object.assign({ component: FollowupDrawer }, NEW_DRAWER_PANEL_OPTIONS, {
          width: newDrawerWidth(viewportWidth()),
          props: {
            teamioPatientID,
            claimNum,
            query,
            detailPageName: rq.detailPageName,
            onClassicDrawer: () => { reopenClassic = true; },
            onRestart: () => restartFollowups(row),
          },
        }));
      } else {
        handle = host.$showPanel(Object.assign({ component: CreateFollowup }, DRAWER_PANEL_OPTIONS, {
          props: {
            teamioPatientID,
            claimNum,
            query,
            detailPageName: rq.detailPageName,
          },
        }));
      }

      handle.promise
        .then(async (result) => {
          // Legacy reads result.Status unguarded: a drawer closed with no result throws there and
          // lands in the rejection branch — no patch, just the cleanup. Same here.
          if (!result) throw new Error('The follow-up panel closed without a result');
          const { target, index } = findRow(row);
          if (result.Status && result.Attempts) {
            if (index !== -1) {
              Vue.set(target, 'Status', result.Status);
              Vue.set(target, 'TeamioFollowupStatusID', result.TeamioFollowupStatusID);
              Vue.set(target, 'FollowupDate', result.FollowupDate);
              Vue.set(target, 'Attempts', result.Attempts);
              Vue.set(target, 'AttemptDetails', result.AttemptDetails);
              Vue.set(target, 'pastDue', 'false');
              Vue.set(target, 'completed', 'true');
              // Patient Profile does not patch Overridden.
              if (!isProfile()) Vue.set(target, 'Overridden', result.Overridden);
              Vue.set(target, result.StatusUpdatedAtPropertyName, result.StatusUpdatedAtPropertyNameValue);
            }
            // The profile has no call goals to move.
            if (!isProfile()) host.$emit('call-goals-updated', index);
          }
          if (index !== -1) Vue.set(target, 'NextVisit', result.nextVisit);
          if (isProfile() && index !== -1) Vue.set(target, 'teamioActive', false);
          setFlag('opening', id, false);
          await release();
          // "Classic drawer": the drawer override now says 'old', so this opens CreateFollowup.vue.
          if (reopenClassic) {
            reopenClassic = false;
            await openDrawer(row);
          }
        })
        .catch(async (error) => {
          console.error('[FOLLOWUP] Follow-up panel promise rejected:', error);
          setFlag('opening', id, false);
          await release();
        });
      return true;
    } catch (err) {
      if (err && err.message !== 'exists') console.error('[FOLLOWUP] Error starting follow-up tracking:', err);
      await release();
      setFlag('opening', id, false);
      setFlag('errors', id, true);

      if (err && err.message === 'exists') {
        // An authorised user gets the toast with the "release the lock" button.
        if (host.$can && host.$can('update', 'followupDate')) {
          host.$toast.error({
            component: ToastComponent,
            props: {
              payload: attemptTrackingPayload,
            },
          }, {
            timeout: 10000,
          });
        } else {
          host.$toast.error('There\'s already someone following-up with this patient');
        }
        const { target, index } = findRow(row);
        if (index !== -1) Vue.set(target, 'teamioActive', false);
      }
      return false;
    }
  }

  /** The Super-Admin AI call button (recall queues). Same request and toasts as onRowAICall. */
  async function aiCall(row) {
    if (!(row.WirelessPhone && row.TxtMsgOk === 1)) return;
    const id = idOf(row);
    setFlag('aiCalling', id, true);
    try {
      const result = await triggerAutorecallCall({
        siteID: host.siteID,
        patNum: row.PatNum,
        taskID: row.TaskID || null,
        callReason: 'recall',
      });
      if (result.success) {
        host.$toast.success(`AI call: ${result.outcome || 'completed'} (${result.mode || 'simulation'})`);
      } else {
        host.$toast.warning(result.message || result.reason || 'AI call did not succeed');
      }
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.message) || err.message;
      host.$toast.error('AI call failed: ' + msg);
    } finally {
      setFlag('aiCalling', id, false);
    }
  }

  // ---- lock cleanup (legacy created / beforeDestroy) -------------------------------------------
  async function cleanupSessions() {
    try {
      await host.$store.dispatch('cache/emergencyCleanupAllSessions');
    } catch (err) {
      console.error('[FOLLOWUP] Emergency cleanup failed:', err);
    }
  }
  function onVisibilityChange() {
    if (document.hidden) cleanupSessions();
  }
  function install() {
    window.addEventListener('beforeunload', cleanupSessions);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  function dispose() {
    try {
      cleanupSessions();
    } catch (e) {
      console.error('[FOLLOWUP] Cleanup error on destroy:', e);
    }
    window.removeEventListener('beforeunload', cleanupSessions);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  return {
    state,
    rowId: idOf,
    isSaving: id => !!state.saving[id],
    hasError: id => !!state.errors[id],
    isOpening: id => !!state.opening[id],
    isAiCalling: id => !!state.aiCalling[id],
    defaultAssignedTo,
    responsibleEmployees,
    preselectedEmployee,
    setInitialDate,
    changeDate,
    removeInitial,
    stopFollowups,
    restartFollowups,
    openDrawer,
    aiCall,
    install,
    dispose,
  };
}
